// Sentry Error Tracking & Monitoring
// Initialize Sentry with proper configuration
// NOTE: Sentry requests may be blocked by adblockers in development
// This is normal and expected. In production, most users don't have adblockers.
import * as Sentry from '@sentry/react';
import { isBenignBrowserMessagePortError } from './benignClientErrors';

let sentryInitialized = false;
let sentryEnabled = true; // Set to false to completely disable Sentry

export const initSentry = () => {
  // Check if Sentry is disabled
  if (!sentryEnabled) {
    console.log('🔇 Sentry disabled (sentryEnabled = false)');
    return;
  }

  // Only initialize in production or if DSN is provided
  let dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || 'development';
  
  // Use hardcoded DSN as fallback for testing
  const testDsn = 'https://67f4aa08a9a6e785ba975703656c6e1b@o4510304679821312.ingest.de.sentry.io/4510304755646544';
  if (!dsn || dsn.includes('old') || dsn.includes('3b793c528bb3')) {
    console.warn('⚠️ [Sentry] Using test DSN (VITE_SENTRY_DSN not set or old)');
    dsn = testDsn;
  }
  
  if (!dsn) {
    console.warn('⚠️ Sentry DSN not configured');
    return;
  }

  // Extract org and project from DSN (supports both .sentry.io and .de.sentry.io)
  const orgMatch = dsn?.match(/@(o?\d+)\.ingest\.(de\.)?sentry\.io/);
  const org = orgMatch ? orgMatch[1] : 'N/A';
  const projectMatch = dsn?.match(/\/(\d+)$/);
  const project = projectMatch ? projectMatch[1] : 'N/A';
  
  console.log('🔍 Sentry init check:', { 
    dsn: dsn ? '✅ Set' : '❌ Missing', 
    environment, 
    dsnValue: dsn ? dsn.substring(0, 30) + '...' : 'N/A',
    org,
    project,
  });

  if (!dsn) {
    console.log('🔇 Sentry disabled (no DSN provided)');
    console.log('   💡 Set VITE_SENTRY_DSN in .env file or Vercel Environment Variables');
    return;
  }

  // Clean DSN: remove whitespace and newlines
  const cleanDsn = dsn.trim().replace(/\s+/g, '');
  if (cleanDsn !== dsn) {
    console.warn('⚠️ [Sentry] DSN contains whitespace, cleaning...');
    // Use cleaned DSN but don't modify original (it's read-only from env)
  }

  if (sentryInitialized) {
    console.log('⚠️ Sentry already initialized');
    return;
  }

  if (!Sentry || typeof Sentry.init !== 'function') {
    console.error('❌ Sentry disabled (package not available)');
    console.error('   💡 Try: npm install @sentry/react');
    return;
  }
  
  console.log('✅ Sentry package loaded successfully');

  try {
    // Clean DSN before using
    const cleanDsn = dsn.trim().replace(/\s+/g, '');
    
    // Validate DSN format (supports both .sentry.io and .de.sentry.io)
    const dsnPattern = /^https:\/\/[a-f0-9]+@o?\d+\.ingest\.(de\.)?sentry\.io\/\d+$/i;
    if (!dsnPattern.test(cleanDsn)) {
      console.error('❌ [Sentry] DSN format is invalid!');
      console.error('   Expected format: https://PUBLIC_KEY@ORG.ingest.sentry.io/PROJECT_ID');
      console.error('   Got:', cleanDsn);
      return;
    }
    
    console.log('🔧 [Sentry] Initializing with DSN:', cleanDsn.substring(0, 50) + '...');
    
    // Production-ready Sentry configuration
    Sentry.init({
      dsn: cleanDsn,
      environment,
      
      // Setting this option to true will send default PII data to Sentry
      sendDefaultPii: true,
      
      // Release tracking - helps group issues by version
      release: `fyle@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
      
      // Performance Monitoring - track page loads and API calls
      integrations: [
        Sentry.browserTracingIntegration({
          // Trace all routes
          enableInp: true, // Track Interaction to Next Paint (INP)
        }),
        Sentry.replayIntegration({
          // Session Replay - record user sessions when errors occur
          maskAllText: true, // Privacy: mask all text
          blockAllMedia: true, // Privacy: block all media
        }),
      ],
      
      // Performance Monitoring Sampling
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
      
      // Session Replay Sampling
      replaysSessionSampleRate: 0, // Don't record all sessions
      replaysOnErrorSampleRate: 1.0, // Record all sessions with errors
      
      // Error filtering - only filter out noise
      beforeSend(event, hint) {
        if (isBenignBrowserMessagePortError(hint.originalException)) {
          return null;
        }
        // Filter out network errors when offline
        if (event.exception) {
          const error = hint.originalException;
          if (error instanceof Error) {
            // Don't report network errors when offline
            if (error.message.includes('Failed to fetch') && !navigator.onLine) {
              return null;
            }
            // Don't report CORS errors (these are configuration issues, not app bugs)
            if (error.message.includes('CORS') || error.message.includes('cross-origin')) {
              return null;
            }
          }
        }
        return event;
      },
      
      // Ignore specific errors
      ignoreErrors: [
        // Browser extension errors
        'top.GLOBALS',
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'atomicFindClose',
        // Network errors that are expected
        'NetworkError',
        'Failed to fetch',
        'Load failed',
        'message channel closed before a response was received',
        'A listener indicated an asynchronous response',
        'Extension context invalidated',
      ],
      
      // Don't send stack traces for these URLs
      denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
      ],
    });

    sentryInitialized = true;
    console.log('✅ Sentry initialized');
    
    // Verify client is ready
    const client = Sentry.getClient();
    if (client) {
      console.log('✅ [Sentry] Client ready');
      console.log('💡 [Sentry] Note: Requests may be blocked by adblockers in development');
    } else {
      console.warn('⚠️ [Sentry] Client not available!');
    }
  } catch (error) {
    console.warn('⚠️ Failed to initialize Sentry:', error);
  }
};

// Capture exceptions manually
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (!sentryInitialized) {
    console.error('Error (Sentry not initialized):', error, context);
    console.error('   💡 Make sure VITE_SENTRY_DSN is set and Sentry.init() was called');
    return;
  }

  try {
    console.log('📤 [Sentry] Capturing exception:', error.message);
    console.log('📤 [Sentry] Context:', context);
    
    const eventId = Sentry.captureException(error, {
      contexts: {
        custom: context || {},
      },
      tags: {
        source: context?.source || 'unknown',
        test: context?.test ? 'true' : 'false',
        // Force unique fingerprint for test events to ensure they create issues
        ...(context?.test ? { testId: context?.uniqueId || `test-${Date.now()}` } : {}),
      },
      // Add fingerprint to force issue creation
      ...(context?.test ? {
        fingerprint: ['test-exception', `test-${context?.uniqueId || Date.now()}`],
      } : {}),
    });
    
    console.log('✅ [Sentry] Exception captured with event ID:', eventId);
    
    // Flush to ensure the event is sent immediately
    console.log('🔄 [Sentry] Calling flush()...');
    console.log('🔍 [Sentry] Client state before flush:', {
      clientExists: !!Sentry.getClient(),
      eventId,
    });
    
    Sentry.flush(5000)
      .then((success) => {
        console.log('✅ [Sentry] Event flushed (sent to server):', success);
        
        // Additional check: Verify client is still active
        const client = Sentry.getClient();
        if (client) {
          console.log('✅ [Sentry] Client is still active after flush');
        } else {
          console.warn('⚠️ [Sentry] Client not available after flush!');
        }
      })
      .catch((err) => {
        console.warn('⚠️ [Sentry] Failed to flush:', err);
      });
    
    // Also try close() as fallback
    setTimeout(() => {
      console.log('🔄 [Sentry] Calling close() as fallback...');
      Sentry.close(2000)
        .then(() => {
          console.log('✅ [Sentry] Close completed');
        })
        .catch((err) => {
          console.warn('⚠️ [Sentry] Close failed:', err);
        });
    }, 1000);
  } catch (err) {
    console.warn('Failed to capture exception:', err);
  }
};

// Capture messages
export const captureMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info') => {
  // Always log to console
  console.log(`[${level.toUpperCase()}] ${message}`);
  
  if (!sentryEnabled) {
    return; // Sentry disabled, just log to console
  }
  
  if (!sentryInitialized) {
    console.warn('⚠️ Sentry not initialized (requests may be blocked by adblocker)');
    return;
  }

  try {
    console.log(`📤 [Sentry] Capturing message (${level}):`, message);
    
    // Use captureMessage with proper level
    const eventId = Sentry.captureMessage(message, {
      level: level === 'error' ? 'error' : level === 'warning' ? 'warning' : 'info',
      tags: {
        source: 'manual',
      },
    });
    console.log('✅ [Sentry] Message captured with event ID:', eventId);
    
    // Flush to ensure the message is sent immediately
    Sentry.flush(5000).catch((err) => {
      console.warn('⚠️ [Sentry] Failed to flush:', err);
    });
  } catch (err) {
    console.warn('Failed to capture message:', err);
  }
};

// Set user context
export const setUser = (user: { id: string; email?: string; username?: string }) => {
  if (!sentryInitialized) return;

  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  } catch (err) {
    console.warn('Failed to set user:', err);
  }
};

// Clear user context
export const clearUser = () => {
  if (!sentryInitialized) return;

  try {
    Sentry.setUser(null);
  } catch (err) {
    console.warn('Failed to clear user:', err);
  }
};

